import { useEffect, useState, useRef } from "react";
import { ArrowClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/dates";

const POLL_MS = 5 * 60 * 1000; // outdated-version check every 5 minutes

// Footer on every authenticated page: Emergent internal version (git
// checkpoint), publication timestamp and the corporate copyright notice.
// Also owns the outdated-version refresh banner.
export function AppFooter() {
  const [info, setInfo] = useState(null);
  const [outdated, setOutdated] = useState(false);
  const initialRevision = useRef(null);

  useEffect(() => {
    let timer;
    const check = async () => {
      try {
        const { data } = await api.get("/system/version");
        if (initialRevision.current === null) {
          initialRevision.current = data.revision;
          setInfo(data);
        } else if (data.revision && data.revision !== initialRevision.current) {
          setOutdated(true);
        }
      } catch {
        // Version info is cosmetic — never disrupt the app if unavailable.
      }
    };
    check();
    timer = setInterval(check, POLL_MS);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      {outdated && (
        <div className="fixed inset-x-0 top-0 z-50 flex items-center justify-center gap-3 bg-[#032852] px-4 py-2.5 text-sm text-white shadow-lg"
          data-testid="version-refresh-banner">
          <span>A new version of REACH has been published. Refresh to load the latest version.</span>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 rounded-sm bg-[#569A1F] px-3 py-1 text-xs font-bold uppercase tracking-wide hover:bg-[#457d18]"
            data-testid="version-refresh-btn"
          >
            <ArrowClockwise size={14} weight="bold" /> Refresh
          </button>
        </div>
      )}
      <footer className="border-t border-[#E4E4E7] bg-white px-6 py-3 text-center" data-testid="app-footer">
        <p className="text-[11px] leading-relaxed text-[#6D6D70]">
          <span data-testid="footer-version">
            REACH · Version {info?.version || "—"}
          </span>
          <span className="mx-1.5 text-[#D4D4D8]">|</span>
          <span data-testid="footer-published">
            Published {info?.published_at ? formatDateTime(info.published_at) : "—"}
          </span>
          <span className="mx-1.5 text-[#D4D4D8]">|</span>
          <span data-testid="footer-copyright">© 2026 Total Facility Solutions, Inc. All rights reserved.</span>
        </p>
      </footer>
    </>
  );
}
